import { t } from '@lingui/core/macro'
import AddIcon from '@mui/icons-material/Add'
import LinkStatusIcon from '@mui/icons-material/CloudDone'
import LinkIcon from '@mui/icons-material/CloudQueue'
import DateIcon from '@mui/icons-material/Event'
import InfoIcon from '@mui/icons-material/Info'
import AutofillIcon from '@mui/icons-material/Link'
import ListIcon from '@mui/icons-material/List'
import LockIcon from '@mui/icons-material/Lock'
import NumberIcon from '@mui/icons-material/LooksOne'
import TextIcon from '@mui/icons-material/TextFormat'
import VPNIcon from '@mui/icons-material/VpnKey'
import Tooltip from '@mui/material/Tooltip'
import { FindAndReplacePopover } from '@components/findAndReplacePopover'
import AiCreativeIcon from 'images/ai-creative.svg'
import styles from './header.css'
import { Theme } from '../../../../../Theme'
import { checkLockedStatus } from '../../utils'

function getIcon(column) {
  const isVariable = column.field.segment_type === 'variable'
  const gov = isVariable ? column : column.field

  if (!column) {
    return ''
  }

  if (column.governance_type === 'cv_id') {
    return <VPNIcon className={styles['icon-override']} />
  }

  if (column.governance_type === 'autonumber' || column.field.governance_type === 'autonumber') {
    return <NumberIcon className={styles['icon-override']} />
  }

  if (column.field.is_pattern) {
    return <VPNIcon className={styles['icon-override']} />
  }

  if (column.field.governance_type === 'concatenation') {
    return <AddIcon className={styles['icon-override']} />
  }

  if (
    column.type === 'autocomplete' &&
    // text with previous value can be a dropdown, so exclude those
    gov.governance_type !== 'text'
  ) {
    return <ListIcon className={styles['icon-override']} />
  }

  if (column.type === 'date') {
    return <DateIcon className={styles['icon-override']} />
  }

  if (column.field.isLinkStatus) {
    return <LinkStatusIcon className={styles['icon-override']} />
  }

  if (column.field.governance_type === 'link') {
    return <LinkIcon className={styles['icon-override']} />
  }

  if (
    column.field.governance_type === 'autofill' ||
    column.governance_type === 'autofill' ||
    column.field.governance_type === 'shortened link' ||
    column.field.governance_type === 'qr-code' ||
    column.field.governance_type === 'deep-link' ||
    column.field.governance_type === 'custom-bitlink'
  ) {
    return <AutofillIcon className={styles['icon-override']} />
  }

  if (column.field.governance_type === 'creative' || column.governance_type === 'creative') {
    return <AiCreativeIcon className={styles['icon-override']} />
  }

  return <TextIcon className={styles['icon-override']} />
}

function getIconText(column) {
  const isVariable = column.field.segment_type === 'variable'
  const gov = isVariable ? column : column.field

  if (!column) {
    return ''
  }

  if (column.governance_type === 'cv_id') {
    return ''
  }

  if (column.field.is_pattern) {
    return t`Pattern`
  }

  if (
    column.type === 'autocomplete' &&
    // text with previous value can be a dropdown, so exclude those
    gov.governance_type !== 'text'
  ) {
    return t`List`
  }

  if (column.type === 'date') {
    return t`Date`
  }

  if (column.field.governance_type === 'concatenation') {
    return t`Concatenation`
  }

  if (column.field.governance_type === 'creative' || column.governance_type === 'creative') {
    return t`Content comprehension`
  }

  if (column.field.governance_type === 'autofill' || column.governance_type === 'autofill') {
    return t`Autofill`
  }

  if (column.field.governance_type === 'link' || column.governance_type === 'link-validation') {
    return t`Link`
  }

  if (column.governance_type === 'autonumber' || column.field.governance_type === 'autonumber') {
    return t`Number`
  }

  if (column.field.governance_type === 'date') {
    return t`Date`
  }

  if (column.field.governance_type === 'autofill') {
    return t`AutoFill`
  }

  if (column.field.governance_type === 'shortened link') {
    return t`Shortened link`
  }

  if (column.field.governance_type === 'qr-code') {
    return t`QR code`
  }

  if (column.field.governance_type === 'deep-link') {
    return t`Deep link`
  }

  if (column.field.governance_type === 'custom-bitlink') {
    return t`Custom Bitly link`
  }

  return t`Text`
}

export const Header = ({ column, hotTable, colIdx }) => {
  if (!column) {
    return <div className={styles.columnHeader} />
  }

  const icon = getIcon(column)
  const descriptionText = column.description || (column.field && column.field.description)
  const iconText = getIconText(column)
  const required = column.required || (column.field && column.field.required)
  const locked = checkLockedStatus(column)
  const listValues = column?.field?.governance_type == 'list' && column.source

  return (
    <Theme>
      <div className={styles.columnHeader}>
        <div className={styles.flex}>
          <div className={styles['row-1']} title={column.field.name}>
            {required ? <span className={styles['required-indicator']}>*</span> : null}
            {column.field.name}
          </div>
          {!locked ? (
            <div className={styles['find-and-replace']}>
              <FindAndReplacePopover hotTable={hotTable} colIdx={colIdx} listValues={listValues} />
            </div>
          ) : null}
        </div>
        <div className={styles['row-2']}>
          <div className={styles['icon-container']}>
            {icon}
            <span className={styles['icon-text']}>{iconText}</span>
          </div>
          <div className={styles.extras}>
            {descriptionText ? (
              <Tooltip
                className={styles['submission-table-header-description-tooltip']}
                delay={300}
                title={descriptionText}
              >
                <InfoIcon className={`${styles['icon-override']} ${styles['icon-info']}`} />
              </Tooltip>
            ) : null}
            {locked ? (
              <Tooltip delay={300} title={t`Column is read-only`}>
                <LockIcon className={`${styles['icon-override']} ${styles['icon-lock']}`} />
              </Tooltip>
            ) : null}
          </div>
        </div>
      </div>
    </Theme>
  )
}
